import React, { useEffect, useState } from 'react';
import { MapPin, Crosshair, X, Globe, ShieldCheck } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import './LocationPromptModal.css';

const LOCATION_MODAL_SEEN_KEY = 'cyber_location_modal_seen_v4';
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Push GPS coords to backend so they are stored in DB & reflected in admin dashboard
const pushGpsToDB = async (sessionId, latitude, longitude, accuracy) => {
  if (!sessionId) return;
  try {
    await fetch(`${API_BASE}/analytics/gps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, latitude, longitude, accuracy })
    });
  } catch (_) {
    // Silently fail — GPS display still works via localStorage
  }
};

export default function LocationPromptModal() {
  const { trackEvent, sessionId } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle', 'requesting', 'granted', 'denied'

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Show popup when user clicks "INITIALIZE SYSTEM"
    const handleSystemInit = () => {
      const hasSeen = sessionStorage.getItem(LOCATION_MODAL_SEEN_KEY);
      if (!hasSeen) {
        // Show custom location modal 1s after initialize system
        setTimeout(() => setIsOpen(true), 800);
      }
    };

    window.addEventListener('initialize-system-clicked', handleSystemInit);
    window.addEventListener('gps_location_captured', () => setStatus('granted'));

    return () => {
      window.removeEventListener('initialize-system-clicked', handleSystemInit);
      window.removeEventListener('gps_location_captured', () => setStatus('granted'));
    };
  }, []);

  const handleGrantLocation = () => {
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setStatus('denied');
      setTimeout(() => setIsOpen(false), 2500);
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setStatus('requesting');
    sessionStorage.setItem(LOCATION_MODAL_SEEN_KEY, 'true');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setStatus('granted');

        // Persist GPS coords so VisitorLocationModal can pick them up immediately
        const gpsPayload = {
          latitude,
          longitude,
          accuracy: Math.round(accuracy),
          timestamp: new Date().toLocaleTimeString()
        };
        try {
          localStorage.setItem('cyber_gps_data', JSON.stringify(gpsPayload));
        } catch (_) {}

        // Push GPS coords to backend DB (store in visits collection)
        pushGpsToDB(sessionId, latitude, longitude, Math.round(accuracy));

        // Dispatch event so any open VisitorLocationModal reacts in real-time
        window.dispatchEvent(new CustomEvent('gps_location_captured', { detail: gpsPayload }));

        if (trackEvent) {
          trackEvent('gps_location_granted', {
            latitude,
            longitude,
            accuracy: Math.round(accuracy),
            category: 'telemetry'
          });
        }

        setTimeout(() => setIsOpen(false), 1500);
      },
      (error) => {
        console.warn('Location permission response:', error.message);
        setStatus('denied');

        if (trackEvent) {
          trackEvent('gps_location_denied', {
            reason: error.message,
            category: 'telemetry'
          });
        }

        setTimeout(() => setIsOpen(false), 2500);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleClose = () => {
    sessionStorage.setItem(LOCATION_MODAL_SEEN_KEY, 'true');
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className="cyber-modal-overlay" onClick={handleClose}>
      <div className="cyber-location-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="location-modal-header">
          <div className="location-modal-title">
            <MapPin className="neon-pulse" size={20} color="#00e5ff" />
            <div>
              <h4>LOCATION TELEMETRY RECON</h4>
              <span>SYS_GPS_SATELLITE_LOCK</span>
            </div>
          </div>
          <button className="close-btn" onClick={handleClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="location-modal-body">
          <p className="modal-desc">
            Allow location access to calibrate high-accuracy satellite GPS telemetry & view live mapping coordinates.
          </p>

          <div className="location-badges">
            <div className="badge-item">
              <ShieldCheck size={14} color="#00ff66" />
              <span>Anonymized & Encrypted</span>
            </div>
            <div className="badge-item">
              <Globe size={14} color="#00e5ff" />
              <span>IP & GPS Resolution</span>
            </div>
          </div>

          {status === 'granted' && (
            <div className="status-banner success">
              🎯 HIGH-ACCURACY GPS LOCKED SUCCESSFULLY!
            </div>
          )}

          {status === 'denied' && (
            <div className="status-banner error">
              ⚠️ Browser location permission blocked/skipped. Using IP Geolocation mode.
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="location-modal-footer">
          <button className="btn-secondary" onClick={handleClose}>
            SKIP / USE IP ONLY
          </button>

          <button 
            className="btn-primary" 
            onClick={handleGrantLocation}
            disabled={status === 'requesting'}
          >
            <Crosshair className={status === 'requesting' ? 'spin-icon' : ''} size={16} />
            {status === 'requesting' ? 'REQUESTING GPS...' : 'ALLOW LOCATION ACCESS'}
          </button>
        </div>

      </div>
    </div>
  );
}
